/**
 * twilioVoice.js — Twilio Voice SDK wrapper for CIVIX Phone Panel
 *
 * Provides:
 *   initVoice(token)   — creates/re-initializes the Device
 *   placeCall(to, params)  — dials a number
 *   acceptIncoming()   — accept incoming call
 *   rejectIncoming()   — reject incoming call
 *   hangUp()           — end active call
 *   mute(bool)         — mute/unmute mic
 *   sendDigits(digits) — DTMF tones
 *   getDevice()        — current Device instance
 *   getActiveCall()    — current Call object
 */

import { Device } from '@twilio/voice-sdk';

let device = null;
let activeCall = null;
let incomingCall = null;
const listeners = new Map();

function emit(event, data) {
  const fns = listeners.get(event) || [];
  fns.forEach((fn) => {
    try { fn(data); } catch (_) {}
  });
}

export function on(event, fn) {
  if (!listeners.has(event)) listeners.set(event, []);
  listeners.get(event).push(fn);
  return () => off(event, fn);
}

export function off(event, fn) {
  if (!listeners.has(event)) return;
  listeners.set(event, listeners.get(event).filter((f) => f !== fn));
}

export async function initVoice(token) {
  if (device) {
    try { device.destroy(); } catch (_) {}
    device = null;
  }
  activeCall = null;
  incomingCall = null;

  // Dev-only stub token (no Twilio Voice SDK creds): fake a registered device so
  // the panel turns green. Calls resolve to an inert call object.
  if (typeof token === 'string' && token.startsWith('simulated_voice_token')) {
    device = {
      state: 'registered',
      simulated: true,
      disconnectAll: () => {},
      destroy: () => {},
      on: () => {},
      connect: () => {
        const handlers = {};
        const call = {
          on: (evt, fn) => { handlers[evt] = fn; },
          disconnect: () => { handlers.disconnect?.({ status: 'closed' }); },
          mute: () => {},
          sendDigits: () => {},
        };
        return Promise.resolve(call);
      },
    };
    setTimeout(() => emit('ready', { simulated: true }), 100);
    return device;
  }

  device = new Device(token, {
    logLevel: 1,
    codecPreferences: ['opus', 'pcmu'],
    fakeLocalDTMF: true,
    enableRingingState: true,
  });

  device.on('registered', () => {
    emit('ready', {});
  });

  device.on('error', (err) => {
    emit('error', { message: err.message || String(err) });
  });

  device.on('incoming', (call) => {
    incomingCall = call;

    // Twilio Voice SDK exposes <Parameter> children of <Client> as
    // call.customParameters (a Map in v2). Power Dialer connects carry:
    //   pdSession   — which Power Dial session this bridge belongs to
    //   pdLead      — the connected lead id (used for lead-card sync)
    //   autoAccept  — 'true' — opt in to auto-accept, no agent tap needed
    // For regular click-to-call these are absent and the popup runs as normal.
    const cp = call.customParameters;
    const readParam = (k) => (cp?.get ? cp.get(k) : cp?.[k]) || '';
    const pdSession = readParam('pdSession');
    const pdLead = readParam('pdLead');
    const autoAccept = readParam('autoAccept') === 'true';

    emit('incoming', {
      from: call.parameters?.From || '',
      callSid: call.parameters?.CallSid || '',
      pdSession,
      pdLead,
      autoAccept,
    });

    call.on('disconnect', () => {
      if (activeCall === call) activeCall = null;
      incomingCall = null;
      emit('ended', { durationSec: 0 });
    });
    call.on('cancel', () => {
      incomingCall = null;
      emit('missed', {});
    });

    // Auto-accept the Power Dialer connect immediately. This eliminates the
    // "agent tap-to-answer" race that lost fast-hanging-up callees. The
    // callee only fired their <Ring> ~200ms ago — accepting instantly means
    // two-way audio starts with the very first "hello."
    if (autoAccept) {
      try {
        call.accept();
        activeCall = call;
        incomingCall = null;
        emit('callAccepted', { pdSession, pdLead, autoAccepted: true });
      } catch (_) {
        // If accept() throws (e.g. already-accepted race), fall through to
        // the manual popup path so the agent can still recover.
      }
    }
  });

  device.on('tokenWillExpire', () => {
    emit('tokenExpiring', {});
  });

  await device.register();
  return device;
}

// Tracks ongoing disconnectAll debounce so we don't flood the SDK
let _disconnectAllTimer = null;

export function placeCall(to, extraParams = {}) {
  if (!device) throw new Error('Twilio Device not initialized. Call initVoice(token) first.');

  // Defensive: kill stale active-call state BEFORE calling device.connect().
  // We debounce disconnectAll() to avoid the repeated spam visible in console logs
  // ([TwilioVoice][Device] .disconnectAll). We only call it ONCE before dialing.
  try {
    if (activeCall) {
      try { activeCall.disconnect(); } catch (_) {}
      activeCall = null;
    }
  } catch (_) {}

  // Single debounced disconnectAll — clears any SDK-tracked legs without spamming.
  // Only runs once; subsequent placeCall invocations within 100ms share the same flush.
  if (_disconnectAllTimer) {
    clearTimeout(_disconnectAllTimer);
    _disconnectAllTimer = null;
  }
  try { device.disconnectAll(); } catch (_) {}

  const call = device.connect({
    params: { To: to, ...extraParams },
  });

  call.then((c) => {
    activeCall = c;

    c.on('ringing', () => emit('ringing', { to }));
    c.on('accept', () => emit('callAccepted', { to }));
    c.on('disconnect', (conn) => {
      activeCall = null;
      const durationSec = conn?.status === 'closed' ? 0 : 0;
      emit('ended', { to, durationSec });
    });
    c.on('error', (err) => emit('error', { message: err.message }));
  }).catch((err) => {
    emit('error', { message: err.message });
  });

  return call;
}

export function acceptIncoming() {
  if (!incomingCall) return;
  incomingCall.accept();
  activeCall = incomingCall;
  incomingCall = null;
  emit('callAccepted', {});
}

export function rejectIncoming() {
  if (!incomingCall) return;
  incomingCall.reject();
  incomingCall = null;
  emit('missed', {});
}

export function hangUp() {
  const hadCall = Boolean(activeCall || incomingCall);
  if (activeCall) {
    try { activeCall.disconnect(); } catch (_) {}
    activeCall = null;
  }
  if (incomingCall) {
    try { incomingCall.reject(); } catch (_) {}
    incomingCall = null;
  }
  if (device && hadCall) {
    try { device.disconnectAll(); } catch (_) {}
  }
  emit('ended', { durationSec: 0 });
}

export function resetActiveCallState() {
  try {
    const hadCall = Boolean(activeCall || incomingCall);
    if (activeCall) {
      try { activeCall.disconnect(); } catch (_) {}
    }
    activeCall = null;
    incomingCall = null;
    if (device && hadCall) {
      try { device.disconnectAll(); } catch (_) {}
    }
    emit('ended', { durationSec: 0, reason: 'recovered' });
    return true;
  } catch (_) {
    return false;
  }
}

export function mute(shouldMute) {
  if (activeCall) {
    activeCall.mute(shouldMute);
    emit('muteChanged', { muted: shouldMute });
  }
}

export function sendDigits(digits) {
  if (activeCall) {
    activeCall.sendDigits(digits);
  }
}

export const PREFERRED_INPUT_KEY = 'civix_preferred_audio_input';
export const PREFERRED_OUTPUT_KEY = 'civix_preferred_audio_output';

/**
 * Enumerate microphones and speakers. Labels are blank until the user has granted
 * microphone permission, so callers may pass { requestPermission: true } once.
 * @param {{requestPermission?: boolean}} [opts]
 * @returns {Promise<{inputs: MediaDeviceInfo[], outputs: MediaDeviceInfo[], outputSupported: boolean}>}
 */
export async function getAudioDevices({ requestPermission = false } = {}) {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
  if (!md?.enumerateDevices) return { inputs: [], outputs: [], outputSupported: false };
  if (requestPermission && md.getUserMedia) {
    try { (await md.getUserMedia({ audio: true })).getTracks().forEach((t) => t.stop()); } catch (_) { /* denied */ }
  }
  const all = await md.enumerateDevices();
  return {
    inputs: all.filter((d) => d.kind === 'audioinput'),
    outputs: all.filter((d) => d.kind === 'audiooutput'),
    // Twilio routes output through setSinkId, which Safari / Firefox lack.
    outputSupported: device?.audio?.isOutputSelectionSupported ?? ('setSinkId' in HTMLMediaElement.prototype),
  };
}

/**
 * @param {string} deviceId - 'default' or a MediaDeviceInfo.deviceId.
 * @returns {Promise<boolean>} True when the Twilio device accepted the microphone.
 */
export async function setAudioInputDevice(deviceId) {
  if (device?.audio?.setInputDevice) {
    try {
      await device.audio.setInputDevice(deviceId || 'default');
      return true;
    } catch (_) {}
  }
  return false;
}

/**
 * Route ringtone + remote audio to a speaker / headset.
 * @param {string} deviceId
 * @returns {Promise<boolean>}
 */
export async function setAudioOutputDevice(deviceId) {
  const speakers = device?.audio?.speakerDevices;
  if (!speakers || device.audio.isOutputSelectionSupported === false) return false;
  try {
    await speakers.set(deviceId || 'default');
    await device.audio.ringtoneDevices?.set(deviceId || 'default');
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Re-apply the saved microphone / speaker (call once the Device is registered).
 * Stale ids (unplugged headset) are ignored so Twilio keeps the system default.
 * @returns {Promise<void>}
 */
export async function applySavedAudioDevices() {
  let input = null;
  let output = null;
  try {
    input = localStorage.getItem(PREFERRED_INPUT_KEY);
    output = localStorage.getItem(PREFERRED_OUTPUT_KEY);
  } catch (_) { return; }
  const { inputs, outputs } = await getAudioDevices();
  if (input && inputs.some((d) => d.deviceId === input)) await setAudioInputDevice(input);
  if (output && outputs.some((d) => d.deviceId === output)) await setAudioOutputDevice(output);
}

export function getDevice() { return device; }
export function getActiveCall() { return activeCall; }
export function getIncomingCall() { return incomingCall; }
export function isReady() { return device?.state === 'registered'; }
